/* eslint-disable react-hooks/exhaustive-deps */
/* PACKAGES */
import { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import { Button, Dialog, DialogBody } from "@material-tailwind/react";
import { toast } from "react-hot-toast";
import { getAllUsers, postPushNotification } from "service/api";
import { IoClose } from "react-icons/io5";

const PushNotificationsNew = () => {
  /* LOADING STATE */
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [notificationTitle, setNotificationTitle] = useState("");
  const [notificationMessage, setNotificationMessage] = useState("");
  const [sendLoading, setSendLoading] = useState(false);

  /* PAGINATION STATE */
  const [page, setPage] = useState(1);
  const [sizePerPage] = useState(20);
  const [totalDocs, setTotalDocs] = useState(0);

  /* USERS LIST */
  const [usersList, setUsersList] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [selectAllFromApi, setSelectAllFromApi] = useState(false);

  /* INFINITE SCROLL */
  const observerRef = useRef(null);
  const sentinelRef = useRef(null);

  /* GET USERS LIST */
  const getUsersList = async () => {
    setLoading(true);

    const data = {
      page: page,
      sizePerPage: sizePerPage,
      pagination: true,
      search: debouncedSearch || undefined,
    };

    try {
      const response = await getAllUsers(data);
      const docs = response?.data?.docs || response?.docs || [];
      const total = response?.data?.totalDocs || response?.totalDocs || 0;
      setUsersList((prev) => (page === 1 ? docs : [...prev, ...docs]));
      setTotalDocs(total);
    } catch (e) {
      console.log("Error: ", e);
    } finally {
      setLoading(false);
    }
  };

  /* USE EFFECT */
  useEffect(() => {
    getUsersList();
  }, [page, sizePerPage, debouncedSearch]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [search]);

  useEffect(() => {
    setPage(1);
    setSelectedUsers(new Set());
    setSelectAllFromApi(false);
  }, [debouncedSearch]);

  useEffect(() => {
    if (!sentinelRef.current) return;
    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const firstEntry = entries[0];
        if (!firstEntry?.isIntersecting) return;
        if (loading) return;
        if (usersList.length >= totalDocs) return;
        setPage((prev) => prev + 1);
      },
      { rootMargin: "200px" }
    );

    observerRef.current.observe(sentinelRef.current);

    return () => observerRef.current?.disconnect();
  }, [loading, totalDocs, usersList.length]);

  const allSelected =
    selectAllFromApi ||
    (usersList.length > 0 &&
      usersList.length === totalDocs &&
      selectedUsers.size === usersList.length);
  const selectedCount = selectAllFromApi
    ? totalDocs || usersList.length
    : selectedUsers.size;

  const toggleUserSelection = (id) => {
    if (!id) return;
    if (selectAllFromApi) {
      setSelectAllFromApi(false);
    }
    setSelectedUsers((prev) => {
      const next = selectAllFromApi
        ? new Set(usersList.map((user) => user?._id).filter(Boolean))
        : new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (usersList.length === 0) return;
    setSelectAllFromApi((prev) => !prev);
    setSelectedUsers((prev) => {
      if (selectAllFromApi) {
        return new Set();
      }
      return new Set(usersList.map((user) => user?._id).filter(Boolean));
    });
  };

  const fetchAllPhoneNumbers = async () => {
    const params = {
      pagination: false,
      search: debouncedSearch || undefined,
    };

    const response = await getAllUsers(params);
    let docs = response?.data?.docs || response?.docs || response?.data || [];

    if (!Array.isArray(docs) || docs.length === 0) {
      if (totalDocs > 0) {
        const fallback = await getAllUsers({
          page: 1,
          sizePerPage: totalDocs,
          pagination: true,
          search: debouncedSearch || undefined,
        });
        docs = fallback?.data?.docs || fallback?.docs || [];
      } else {
        docs = [];
      }
    }

    return docs.map((user) => user?.phoneNumber).filter(Boolean);
  };

  const handleSendNotification = async (event) => {
    event.preventDefault();

    const title = notificationTitle.trim();
    const message = notificationMessage.trim();

    if (!title || !message) {
      toast.error("Please enter a title and message.");
      return;
    }

    try {
      setSendLoading(true);
      let phoneNumbers = [];

      if (selectAllFromApi) {
        phoneNumbers = await fetchAllPhoneNumbers();
      } else {
        phoneNumbers = usersList
          .filter((user) => selectedUsers.has(user?._id))
          .map((user) => user?.phoneNumber)
          .filter(Boolean);
      }

      if (phoneNumbers.length === 0) {
        toast.error("No users selected to send notifications.");
        return;
      }

      await postPushNotification({
        phoneNumber: phoneNumbers,
        title,
        message,
      });

      toast.success("Push notification sent.");
      setSendDialogOpen(false);
      setNotificationTitle("");
      setNotificationMessage("");
      setSelectedUsers(new Set());
      setSelectAllFromApi(false);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to send notification.");
    } finally {
      setSendLoading(false);
    }
  };

  return (
    <section className="p-2 xl:p-5 lg:p-5 min-h-screen xl:min-h-full lg:min-h-full">
      {/* PAGE TITLE */}
      <Helmet>
        <title>Push Notifications | LIBF</title>
      </Helmet>

      <div className="container-full-width">
        {/* HEADING */}
        <div className="block xl:flex lg:flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-ibm-bold text-black tracking-tight">
              Push Notifications
            </h1>
            <h2 className="text-base font-ibm-regular text-greyText">
              Send push notifications to the registered users of the app.
            </h2>
          </div>
          <div className="flex w-full max-w-xl items-center justify-end gap-3">
            <input
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search user by name"
              className="w-full max-w-xs rounded-lg border border-greyText/30 bg-white px-4 py-3 text-sm font-ibm-regular text-black placeholder:text-greyText/70 focus:border-black focus:outline-none"
            />
            <Button
              className="rounded-lg bg-black px-4 py-3 text-sm font-ibm-bold uppercase tracking-wide shadow-none hover:shadow-none"
              onClick={() => setSendDialogOpen(true)}
            >
              Send push notification
            </Button>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between rounded-lg border border-greyText/20 bg-white px-4 py-3">
          <label className="flex items-center gap-2 text-sm font-ibm-regular text-black">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={toggleSelectAll}
              className="h-4 w-4"
            />
            Select all
          </label>
          {allSelected ? (
            <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-ibm-bold text-green-700">
              All users selected
              {selectedCount ? ` (${selectedCount})` : ""}
            </span>
          ) : (
            <span className="text-xs font-ibm-regular text-greyText">
              {selectedCount} selected
            </span>
          )}
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {usersList.map((user) => {
            const hasToken = Boolean(user?.fireBaseToken);
            const isSelected = selectAllFromApi || selectedUsers.has(user?._id);
            return (
              <div
                key={user?._id || `${user?.firstName}-${user?.phoneNumber}`}
                className={`rounded-xl border p-4 shadow-sm ${
                  isSelected
                    ? "border-green-400 bg-green-50/40"
                    : "border-greyText/20 bg-white"
                }`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-ibm-regular text-greyText">
                      Name
                    </p>
                    <p className="text-lg font-ibm-bold text-black">
                      {user?.firstName || "Unknown"} {user?.lastName || ""}
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleUserSelection(user?._id)}
                    className="mt-1 h-4 w-4"
                  />
                </div>
                <div className="mt-2">
                  <span
                    className={`rounded-full px-3 py-1 text-xs font-ibm-bold ${
                      hasToken
                        ? "bg-green-100 text-green-700"
                        : "bg-greyText/10 text-greyText"
                    }`}
                  >
                    {hasToken ? "Active" : "Inactive"}
                  </span>
                </div>

                <div className="mt-3">
                  <p className="text-sm font-ibm-regular text-greyText">
                    Phone Number
                  </p>
                  <p className="text-base font-ibm-regular text-black">
                    {user?.phoneNumber || "Not provided"}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        <div ref={sentinelRef} className="h-8" />
        {loading && (
          <p className="mt-4 text-center text-sm font-ibm-regular text-greyText">
            Loading more users...
          </p>
        )}
        {!loading && usersList.length === 0 && (
          <p className="mt-4 text-center text-sm font-ibm-regular text-greyText">
            No users found.
          </p>
        )}
      </div>

      <Dialog
        open={sendDialogOpen}
        handler={() => setSendDialogOpen(!sendDialogOpen)}
        size="xs"
        animate={{
          mount: { scale: 1, y: 0 },
          unmount: { scale: 0.95, y: -50 },
        }}
      >
        <DialogBody className="p-0">
          <div className="border-b border-greyText/20 px-5 py-3 flex items-center justify-between">
            <h5 className="text-lg font-ibm-bold text-black tracking-tight">
              Send Push Notification
            </h5>
            <Button
              className="p-2 rounded-full bg-white shadow-none hover:shadow-none hover:bg-black hover:bg-opacity-5"
              onClick={() => setSendDialogOpen(false)}
            >
              <IoClose className="w-4 h-4 text-greyText" />
            </Button>
          </div>
          <form onSubmit={handleSendNotification} className="p-5">
            <div className="space-y-4">
              <div>
                <label className="text-sm font-ibm-regular text-greyText">
                  Title
                </label>
                <input
                  type="text"
                  value={notificationTitle}
                  onChange={(event) => setNotificationTitle(event.target.value)}
                  placeholder="Enter notification title"
                  className="mt-2 w-full rounded-lg border border-greyText/30 bg-white px-4 py-2 text-sm font-ibm-regular text-black focus:border-black focus:outline-none"
                  required
                />
              </div>
              <div>
                <label className="text-sm font-ibm-regular text-greyText">
                  Message
                </label>
                <textarea
                  value={notificationMessage}
                  onChange={(event) =>
                    setNotificationMessage(event.target.value)
                  }
                  placeholder="Enter notification message"
                  rows={4}
                  className="mt-2 w-full rounded-lg border border-greyText/30 bg-white px-4 py-2 text-sm font-ibm-regular text-black focus:border-black focus:outline-none"
                  required
                />
              </div>
            </div>
            <div className="mt-5 flex items-center justify-end gap-2">
              <Button
                type="button"
                className="rounded-lg bg-white px-4 py-2 text-sm font-ibm-bold uppercase tracking-wide text-black border border-greyText/30 shadow-none hover:shadow-none"
                onClick={() => setSendDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={sendLoading}
                className="rounded-lg bg-black px-4 py-2 text-sm font-ibm-bold uppercase tracking-wide shadow-none hover:shadow-none disabled:opacity-60"
              >
                {sendLoading ? "Sending..." : "Send"}
              </Button>
            </div>
          </form>
        </DialogBody>
      </Dialog>
    </section>
  );
};

export default PushNotificationsNew;
